import { CometChat } from "@cometchat/chat-sdk-javascript";

/**
 * Manual status (preset + optional reason) — distinct from CometChat's own
 * real-time connection presence (the small green/amber corner dot, driven
 * by getStatus()). Both coexist, same as Slack/Teams: a person can be
 * "online" (connected) while their status says "Out of office · back Mon".
 * Stored in user metadata so it's visible to anyone who looks up the user.
 */

export type StatusPreset = "online" | "away" | "offline" | "ooo";

export type UserStatus = { preset: StatusPreset; reason?: string };

export const STATUS_PRESETS: Record<StatusPreset, { label: string; color: string }> = {
  online: { label: "Online", color: "var(--dbm-green)" },
  away: { label: "Away", color: "var(--dbm-amber)" },
  offline: { label: "Offline", color: "var(--dbm-steel-dim)" },
  ooo: { label: "Out of office", color: "#ff5a4d" },
};

export function parseMeta(user: CometChat.User | null | undefined): Record<string, any> {
  if (!user) return {};
  let meta: any = user.getMetadata?.() || {};
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = {};
    }
  }
  return meta && typeof meta === "object" ? meta : {};
}

export function readStatus(user: CometChat.User | null | undefined): UserStatus | null {
  const s = parseMeta(user).dbmStatus;
  if (!s || !s.preset || !(s.preset in STATUS_PRESETS)) return null;
  return { preset: s.preset, reason: s.reason || undefined };
}

export function readPhone(user: CometChat.User | null | undefined): { phone?: string; visible: boolean } {
  const meta = parseMeta(user);
  return { phone: meta.dbmPhone || undefined, visible: !!meta.dbmPhoneVisible };
}

/** Job title, self-set in the profile. Unlike the phone number there's no
 * visibility toggle — a designation only exists to tell colleagues who
 * they're talking to, so it's always shown wherever the person appears. */
export function readDesignation(user: CometChat.User | null | undefined): string | undefined {
  const d = parseMeta(user).dbmDesignation;
  return typeof d === "string" && d.trim() ? d.trim() : undefined;
}

/** Merges the given keys into the current user's metadata and saves it. */
export async function updateMyMetadata(patch: Record<string, unknown>): Promise<void> {
  const current = await CometChat.getLoggedinUser();
  if (!current) return;
  const meta = parseMeta(current);
  Object.assign(meta, patch);
  (current as any).setMetadata(meta);
  await CometChat.updateCurrentUserDetails(current);
}
